import { useDispatch } from "Hooks/useDispatch";
import useReduxState from "Hooks/useReduxState";
import { getAssessmentTypeOptions } from "../modules/Utils";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { useI18n } from "Hooks";
import {
  UBD_ASSIGNMENT_TYPE_MAPPING_FOR_CALENDAR,
  VIDEO_MEETING,
} from "ClassRoom/modules/ClassRoomModule";
import { getDynamicAssessmentTypeOptions } from "ClassRoom/modules/utils";
import { getSelectedSchoolDetailsForCalendar } from "MyCalendar/modules/Utils";
import _ from "lodash";
import ACLStore from "lib/aclStore";
import { getIsAssessmentTypeConfigurationEnabled } from "Utils/ACLStoreUtils";

/**
 * Combines default static assessment types with VIDEO_MEETING for FF-off path.
 * @param {Object} assessmentTypes - Assessment types from getCurrentAssessmentTypes
 * @returns {Object} Combined assessment types filtered by permissions
 */
const combineDefaultAssessmentTypes = assessmentTypes => {
  const {
    le,
    qt,
    worksheet,
    ai_tutor,
    ...rest
  } = _.omit(UBD_ASSIGNMENT_TYPE_MAPPING_FOR_CALENDAR, ["as"]);

  const combinedAssessmentTypes = {
    le,
    qt,
    ...assessmentTypes,
    worksheet,
    ai_tutor,
    VIDEO_MEETING,
    ...rest,
  };

  return _.reduce(
    combinedAssessmentTypes,
    (finalAssessmentTypes, value, uid) => {
      const { perm } = value;
      if (!perm || ACLStore.can(perm)) {
        finalAssessmentTypes[uid] = value;
      }
      return finalAssessmentTypes;
    },
    {}
  );
};

/**
 * Transforms dynamic assessment type options into the calendar tag format.
 * Uses taskTypeId as keys for filtering with taskTypeIds array.
 * @param {Array} dynamicOptions - Options from getDynamicAssessmentTypeOptions
 * @returns {Object} Assessment types keyed by taskTypeId for calendar filtering
 */
const getDynamicCalendarAssessmentTags = dynamicOptions => {
  const dynamicAssessmentTypes = _.reduce(
    dynamicOptions,
    (calendarTagsByTaskTypeId, option) => {
      const taskTypeId = _.get(option, "taskTypeId");
      const perm = _.get(option, "perm");
      if (perm && !ACLStore.can(perm)) return calendarTagsByTaskTypeId;
      calendarTagsByTaskTypeId[taskTypeId] = {
        typeLabel: _.get(option, "label", ""),
        label: _.get(option, "label", ""),
        color: _.get(option, "color", ""),
        uid: taskTypeId,
        taskType: _.get(option, "taskType", ""),
        isEnabled: true,
      };
      return calendarTagsByTaskTypeId;
    },
    {}
  );

  // Add VIDEO_MEETING tag
  const videoPerm = _.get(VIDEO_MEETING, "perm");
  if (!videoPerm || ACLStore.can(videoPerm)) {
    dynamicAssessmentTypes["VIDEO_MEETING"] = VIDEO_MEETING;
  }

  return dynamicAssessmentTypes;
};

const useAssessmentTags = () => {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();
  const { currentCurriculumProgramId } = useReduxState(
    getSelectedSchoolDetailsForCalendar
  );
  const assessmentTypes = dispatch(getCurrentAssessmentTypes());

  // When FF is on, use dynamic assessment type options
  if (isAssessmentTypeConfigurationEnabled) {
    const dynamicOptions = getDynamicAssessmentTypeOptions({
      curriculumId: currentCurriculumProgramId,
    });

    const filteredAssessmentTypes = getDynamicCalendarAssessmentTags(
      dynamicOptions
    );

    const assessmentTypeOptionsToShow = _.reduce(
      dynamicOptions,
      (options, option) => {
        const perm = _.get(option, "perm");
        if (perm && !ACLStore.can(perm)) return options;
        return [
          ...options,
          {
            label: _.get(option, "label"),
            value: _.get(option, "taskTypeId"),
          },
        ];
      },
      []
    );

    return {
      options: assessmentTypeOptionsToShow,
      assessmentTypes,
      filteredAssessmentTypes,
    };
  }

  // When FF is off, use static assessment type configuration
  const {
    assessmentTypeOptionsToShow,
    filteredAssessmentTypes,
  } = getAssessmentTypeOptions({
    t,
    getCurrentAssessmentTypes: () => assessmentTypes,
  });

  return {
    options: assessmentTypeOptionsToShow,
    assessmentTypes,
    filteredAssessmentTypes: combineDefaultAssessmentTypes(
      filteredAssessmentTypes
    ),
  };
};

export default useAssessmentTags;
